import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Reveal from "@/components/Reveal";
import { postOrder, createCheckout } from "@/lib/api";
import { ShoppingBag, X, Minus, Plus, Send, CreditCard, Coffee, ShoppingBasket, CupSoda, Frame } from "lucide-react";
import { toast } from "sonner";

const PRODUCTS = [
  { id: "espresso-tin", name: "Espresso Ground Coffee Tin", price: 8.0, img: "/coming-soon.png", icon: Coffee, tag: "Pantry", desc: "Our own 100% Arabica family blend, ground and sealed in the purple tin." },
  { id: "cantuccini", name: "Cantuccini Gift Box", price: 6.95, img: "/coming-soon.png", icon: Coffee, tag: "Pantry", desc: "Twice-baked almond biscuits, boxed and ribboned." },
  { id: "vintage-print", name: "Vintage Parlour Print · A3", price: 12.0, img: "/coming-soon.png", icon: Frame, tag: "Prints", desc: "The original frontage, giant cone and all." },
  { id: "printed-magnets", name: "Printed Magnets", price: 3.0, img: "/coming-soon.png", icon: ShoppingBasket, tag: "Souvenirs", desc: "Parlour artwork for your fridge — a little seaside magic at home." },
  { id: "postcards", name: "Postcards", price: 1.5, img: "/coming-soon.png", icon: Send, tag: "Souvenirs", desc: "Vintage Broadstairs scenes, ready to send with love." },
  { id: "seaside-bundle", name: "Seaside Gift Bundle", price: 24.99, img: "/coming-soon.png", icon: ShoppingBag, tag: "Gift Sets", desc: "Two magnets, three postcards, a tin of our espresso ground coffee and a signature keychain — worth £27.00, yours for £24.99." },
  { id: "cornet-keychain", name: "Signature Cornet Keychain", price: 8.5, img: "/shop-keychain.webp", icon: ShoppingBasket, tag: "Souvenirs", desc: "Our iconic cone in miniature — carry Morelli's everywhere." },
];

const Shop = () => {
  const SHOP_OPEN = true;

  const [basket, setBasket] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [fulfilment, setFulfilment] = useState("collection");
  const [addr, setAddr] = useState({ line1: "", line2: "", city: "", postcode: "" });
  const [sending, setSending] = useState(false);
  const [orderRef, setOrderRef] = useState(null);

  const add = (p) => {
    setBasket((b) => {
      const found = b.find((i) => i.id === p.id);
      if (found) return b.map((i) => (i.id === p.id ? { ...i, qty: i.qty + 1 } : i));
      return [...b, { ...p, qty: 1 }];
    });
    setDrawerOpen(true);
  };

  const changeQty = (id, d) => {
    setBasket((b) => b.map((i) => (i.id === id ? { ...i, qty: i.qty + d } : i)).filter((i) => i.qty > 0));
  };

  const DELIVERY_FEE = 1.5;
  const itemsTotal = basket.reduce((sum, i) => sum + i.price * i.qty, 0);
  const total = fulfilment === "delivery" ? itemsTotal + DELIVERY_FEE : itemsTotal;
  const count = basket.reduce((sum, i) => sum + i.qty, 0);

  const buildOrder = () => postOrder({
    name: form.name,
    email: form.email,
    items: basket.map((i) => ({ name: i.name, price: i.price, qty: i.qty })),
    total: Number(total.toFixed(2)),
    fulfilment,
    delivery_fee: fulfilment === "delivery" ? DELIVERY_FEE : 0,
    ...(fulfilment === "delivery" ? { address: addr } : {}),
  });

  const checkout = async (e) => {
    e.preventDefault();
    if (sending || !basket.length) return;
    setSending(true);
    try {
      const order = await buildOrder();
      setOrderRef(order.id.slice(0, 8).toUpperCase());
      setBasket([]);
      toast.success("Reserved! Pay & collect in the parlour.");
    } catch {
      toast.error("Something went wrong — please try again");
    } finally {
      setSending(false);
    }
  };

  const checkoutCard = async (e) => {
    e.preventDefault();
    if (sending || !basket.length) return;
    if (!form.name || !form.email) {
      toast.error("Add your name and email first");
      return;
    }
    setSending(true);
    try {
      const order = await buildOrder();
      const session = await createCheckout({
        order_id: order.id,
        origin_url: window.location.origin,
        items: basket.map((i) => ({ lookup_key: i.id.replace(/-/g, "_"), qty: i.qty })),
        delivery: fulfilment === "delivery",
      });
      window.location.href = session.checkout_url;
    } catch {
      toast.error("Card checkout unavailable — try Reserve & Collect");
      setSending(false);
    }
  };

  if (!SHOP_OPEN) {
    return (
      <div className="min-h-screen bg-cream pt-32 pb-24 flex items-center justify-center" data-testid="shop-wip">
        <Reveal className="text-center px-6 max-w-lg mx-auto">
          <p className="text-xs uppercase tracking-[0.4em] text-teal/60 font-medium">The Morelli&rsquo;s Shop</p>
          <h1 className="font-display font-bold text-4xl md:text-5xl mt-4 text-teal">Work in progress</h1>
          <div className="mt-6 h-px w-20 bg-gold mx-auto" />
          <p className="mt-6 text-teal/70 leading-relaxed">
            Our little online shop is still being polished behind the counter. Pop back soon —
            or visit us in person at 14 Victoria Parade for gifts, tins and treats.
          </p>
          <a href="/" data-testid="shop-wip-home-link"
            className="mt-8 inline-block rounded-full bg-teal text-cream font-semibold uppercase tracking-[0.2em] text-sm px-10 py-4 hover:bg-gold hover:text-teal-deep transition-colors duration-300">
            Back to the Parlour
          </a>
        </Reveal>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pt-32 pb-24" data-testid="shop-page">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal className="text-center">
          <p className="text-xs uppercase tracking-[0.4em] text-teal/70 font-medium">The Morelli&rsquo;s Shop</p>
          <h1 className="font-display font-bold tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl mt-4 text-teal">
            Take the Parlour <span className="italic text-transparent bg-clip-text bg-gradient-to-r from-gold via-[#d9b878] to-gold">Home</span>
          </h1>
          <div className="mt-6 h-px w-24 bg-gold mx-auto" />
          <p className="mt-6 max-w-md mx-auto text-teal/70 text-sm md:text-base leading-relaxed">
            Merchandise, prints and pantry favourites. Reserve online, then pay &amp; collect at 14 Victoria Parade.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 0.07}>
              <article data-testid={`product-${p.id}`} className="group bg-white rounded-[1.75rem] overflow-hidden border border-gold/25 shadow-md shadow-teal/10 hover:-translate-y-2 hover:border-gold/60 transition-[transform,border-color] duration-500 flex flex-col h-full">
                <div className={`relative h-52 overflow-hidden ${p.img ? "" : p.tile}`}>
                  {p.img ? (
                    <img src={p.img} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  ) : (
                    <div className="w-full h-full grid place-items-center">
                      <img src="/morellis-logo-white.png" alt="" aria-hidden="true" className="w-2/3 opacity-90" />
                    </div>
                  )}
                  <span className="absolute top-4 left-4 bg-cream/90 text-teal text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-full">{p.tag}</span>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-display font-bold text-lg text-teal leading-tight">{p.name}</h3>
                    <span className="font-display font-semibold text-gold whitespace-nowrap">£{p.price.toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-teal/60 mt-1.5 leading-relaxed flex-1">{p.desc}</p>
                  <button onClick={() => add(p)} data-testid={`add-${p.id}`}
                    className="mt-4 w-full rounded-full bg-teal text-cream text-xs uppercase tracking-[0.2em] py-3 hover:bg-gold hover:text-teal-deep transition-colors duration-300 active:scale-95 flex items-center justify-center gap-2">
                    <ShoppingBag size={14} /> Add to Basket
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>

      <button onClick={() => setDrawerOpen(true)} data-testid="basket-open-button" aria-label="Open basket"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-teal text-cream grid place-items-center shadow-xl shadow-teal/30 hover:bg-gold hover:text-teal-deep transition-colors duration-300 active:scale-95">
        <ShoppingBag size={22} />
        {count > 0 && (
          <span data-testid="basket-count" className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-gold text-teal-deep text-xs font-bold grid place-items-center">{count}</span>
        )}
      </button>

      <AnimatePresence>
        {drawerOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setDrawerOpen(false)} className="fixed inset-0 bg-teal-deep/50 backdrop-blur-sm z-50" />
            <motion.aside initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 260, damping: 30 }}
              data-testid="basket-drawer"
              className="fixed top-0 right-0 h-full w-full max-w-md bg-cream z-50 shadow-2xl flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gold/30">
                <h2 className="font-display font-bold text-2xl text-teal">Your Basket</h2>
                <button onClick={() => setDrawerOpen(false)} data-testid="basket-close-button" aria-label="Close basket"
                  className="w-10 h-10 grid place-items-center rounded-full border border-teal/20 hover:bg-mint transition-colors"><X size={18} /></button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {orderRef ? (
                  <div className="text-center py-16" data-testid="order-confirmation">
                    <p className="font-display italic text-3xl text-teal">Grazie!</p>
                    <p className="mt-4 text-sm text-teal/70 leading-relaxed">Your reservation reference is</p>
                    <p className="font-display font-black text-4xl text-gold mt-2" data-testid="order-ref">{orderRef}</p>
                    <p className="mt-4 text-sm text-teal/70 leading-relaxed max-w-xs mx-auto">Show this at the counter to pay &amp; collect. We&rsquo;ll keep it aside for 48 hours.</p>
                    <button onClick={() => { setOrderRef(null); setDrawerOpen(false); }} data-testid="order-continue-button"
                      className="mt-8 rounded-full bg-teal text-cream px-7 py-3 text-xs uppercase tracking-[0.2em] hover:bg-gold hover:text-teal-deep transition-colors">Continue Browsing</button>
                  </div>
                ) : basket.length === 0 ? (
                  <p className="text-center text-teal/50 italic font-display text-xl py-16" data-testid="basket-empty">Your basket is empty — for now.</p>
                ) : (
                  <ul className="flex flex-col gap-4">
                    {basket.map((i) => (
                      <li key={i.id} data-testid={`basket-item-${i.id}`} className="flex items-center gap-4 bg-white rounded-2xl p-3 border border-gold/20">
                        <div className={`w-14 h-14 rounded-xl overflow-hidden shrink-0 ${i.img ? "" : i.tile + " grid place-items-center"}`}>
                          {i.img ? <img src={i.img} alt="" className="w-full h-full object-cover" /> : <i.icon size={20} className="text-cream" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-display font-bold text-teal text-sm truncate">{i.name}</p>
                          <p className="text-xs text-gold font-semibold">£{i.price.toFixed(2)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => changeQty(i.id, -1)} data-testid={`qty-minus-${i.id}`} aria-label="Decrease" className="w-7 h-7 rounded-full border border-teal/25 grid place-items-center hover:bg-mint transition-colors"><Minus size={12} /></button>
                          <span className="w-5 text-center text-sm font-semibold text-teal">{i.qty}</span>
                          <button onClick={() => changeQty(i.id, 1)} data-testid={`qty-plus-${i.id}`} aria-label="Increase" className="w-7 h-7 rounded-full border border-teal/25 grid place-items-center hover:bg-mint transition-colors"><Plus size={12} /></button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {!orderRef && basket.length > 0 && (
                <form onSubmit={checkout} data-testid="checkout-form" className="p-6 border-t border-gold/30 bg-white/60">
                  <div className="flex flex-col gap-1.5 mb-4">
                    {fulfilment === "delivery" && (
                      <div className="flex justify-between items-baseline" data-testid="delivery-fee-row">
                        <span className="text-xs uppercase tracking-[0.25em] text-teal/60">Delivery</span>
                        <span className="text-sm font-semibold text-teal">£{DELIVERY_FEE.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs uppercase tracking-[0.25em] text-teal/60">Total</span>
                      <span className="font-display font-black text-2xl text-teal" data-testid="basket-total">£{total.toFixed(2)}</span>
                    </div>
                  </div>
                  <input required maxLength={60} placeholder="Your name" data-testid="checkout-name" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-transparent border-b border-teal/30 focus:border-gold focus:outline-none py-2 mb-3 text-teal placeholder:text-teal/40 transition-colors" />
                  <input required type="email" placeholder="Email address" data-testid="checkout-email" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-transparent border-b border-teal/30 focus:border-gold focus:outline-none py-2 mb-4 text-teal placeholder:text-teal/40 transition-colors" />
                  <div className="grid grid-cols-2 gap-2 mb-4" role="radiogroup" aria-label="Fulfilment">
                    {[["collection", "Collect in Parlour"], ["delivery", "Deliver to Me"]].map(([v, label]) => (
                      <button key={v} type="button" data-testid={`fulfilment-${v}`} onClick={() => setFulfilment(v)}
                        aria-pressed={fulfilment === v}
                        className={`rounded-full py-2.5 text-xs uppercase tracking-[0.15em] font-medium transition-colors duration-300 ${fulfilment === v ? "bg-teal text-cream" : "border border-teal/30 text-teal/70 hover:border-gold"}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                  {fulfilment === "delivery" && (
                    <div data-testid="delivery-fields" className="flex flex-col gap-3 mb-4">
                      <input required maxLength={120} placeholder="Address line 1" data-testid="address-line1" value={addr.line1}
                        onChange={(e) => setAddr({ ...addr, line1: e.target.value })}
                        className="w-full bg-transparent border-b border-teal/30 focus:border-gold focus:outline-none py-2 text-teal placeholder:text-teal/40 transition-colors" />
                      <input maxLength={120} placeholder="Address line 2 (optional)" data-testid="address-line2" value={addr.line2}
                        onChange={(e) => setAddr({ ...addr, line2: e.target.value })}
                        className="w-full bg-transparent border-b border-teal/30 focus:border-gold focus:outline-none py-2 text-teal placeholder:text-teal/40 transition-colors" />
                      <div className="grid grid-cols-2 gap-3">
                        <input required maxLength={60} placeholder="Town / City" data-testid="address-city" value={addr.city}
                          onChange={(e) => setAddr({ ...addr, city: e.target.value })}
                          className="w-full bg-transparent border-b border-teal/30 focus:border-gold focus:outline-none py-2 text-teal placeholder:text-teal/40 transition-colors" />
                        <input required maxLength={10} placeholder="Postcode" data-testid="address-postcode" value={addr.postcode}
                          onChange={(e) => setAddr({ ...addr, postcode: e.target.value })}
                          className="w-full bg-transparent border-b border-teal/30 focus:border-gold focus:outline-none py-2 text-teal placeholder:text-teal/40 transition-colors" />
                      </div>
                    </div>
                  )}
                  <div className="flex flex-col gap-3">
                    <button type="button" onClick={checkoutCard} disabled={sending} data-testid="checkout-card-button"
                      className="w-full rounded-full bg-teal text-cream font-semibold uppercase tracking-[0.2em] text-sm py-4 flex items-center justify-center gap-2 hover:bg-gold hover:text-teal-deep transition-colors duration-300 active:scale-95 disabled:opacity-60">
                      {sending ? "One moment..." : <>Pay by Card Now <CreditCard size={15} /></>}
                    </button>
                    <button type="submit" disabled={sending} data-testid="checkout-submit-button"
                      className="w-full rounded-full border-2 border-teal/60 text-teal font-semibold uppercase tracking-[0.2em] text-sm py-3.5 flex items-center justify-center gap-2 hover:border-gold hover:text-gold transition-colors duration-300 active:scale-95 disabled:opacity-60">
                      {sending ? "Reserving..." : <>Reserve & Collect <Send size={15} /></>}
                    </button>
                  </div>
                  <p className="text-[11px] text-teal/50 text-center mt-3">{fulfilment === "delivery" ? "UK delivery £1.50 — pay by card now, or reserve and pay at the counter." : "Pay securely by card now, or reserve free and pay at the counter."}</p>
                </form>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Shop;
